/**
 * QuotePDF — quote document for @react-pdf/renderer.
 *
 * Deliberately mirrors InvoicePDF's layout so quotes and invoices feel like one
 * family, but drops the payment/balance/bank blocks (a quote is not payable) and
 * relabels the document as a QUOTE with a "Valid Until" date. The Approve/Decline
 * call-to-action lives in the email body, not the PDF.
 */
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { PdfPageFooter } from '../../../lib/PdfPageFooter';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

import { mileageLine } from '../Customers/mileage';
import { DOC } from '../../../lib/documentPalette';

const C = DOC;

const s = StyleSheet.create({
    page: { fontFamily: 'Helvetica', fontSize: 9, lineHeight: 1.4, color: C.body, backgroundColor: C.white, paddingBottom: 40 },

    continuationHeader: { position: 'absolute', top: 0, left: 0, right: 0, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '8 35', backgroundColor: C.bg, borderBottom: `1px solid ${C.line}` },
    continuationText: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.muted },

    header: { padding: '24 35 18 35', borderBottom: `1px solid ${C.line}` },
    logoRow: { marginBottom: 16 },
    logoBox: { width: 48, height: 48 },
    logo: { width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 },

    row: { flexDirection: 'row', alignItems: 'flex-start' },
    col1Left: { flex: 0.7, flexShrink: 0, flexGrow: 0.7, paddingRight: 45 },
    col2Mid: { flex: 1.3, flexShrink: 0, flexGrow: 1.3, paddingRight: 16 },
    colLast: { flex: 1, flexShrink: 0, flexGrow: 1, alignItems: 'flex-end' },

    label: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 },
    h1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 2 },
    h2: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 2 },
    sm: { fontSize: 8, color: C.muted, marginBottom: 1.5 },
    vat: { fontSize: 7, color: C.label, marginTop: 4 },

    vehicleLine: { fontSize: 8, color: C.muted, marginTop: 8, paddingTop: 5, borderTop: `1px solid ${C.faintLine}` },
    vrm: { fontFamily: 'Helvetica-Bold', color: C.black },

    docTitle: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: C.black, letterSpacing: 1.5, textTransform: 'uppercase', textAlign: 'right', marginBottom: 2 },
    docNumber: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.muted, textAlign: 'right', marginBottom: 0 },

    metaBox: { alignItems: 'flex-end', marginTop: 10 },
    metaRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 4 },
    metaLabel: { fontSize: 7, color: C.label, marginRight: 6 },
    metaVal: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: C.black, textAlign: 'right' },

    table: { padding: '14 35 6 35' },
    thead: { flexDirection: 'row', borderBottom: `2px solid ${C.line}`, paddingBottom: 6, marginBottom: 2 },
    th: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 0.6 },
    tr: { flexDirection: 'row', paddingVertical: 7, borderBottom: `1px solid ${C.faintLine}` },
    cDesc: { flex: 3 },
    cQty: { flex: 0.5, textAlign: 'right' },
    cPrice: { flex: 1, textAlign: 'right' },
    cTotal: { flex: 1, textAlign: 'right' },

    itemName: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black },
    badge: { padding: '1.5 4', borderRadius: 2, marginLeft: 5 },
    badgeText: { fontSize: 5.5, textTransform: 'uppercase', fontFamily: 'Helvetica-Bold', letterSpacing: 0.4 },

    totalsBox: { padding: '14 35', backgroundColor: C.bg, borderTop: `1px solid ${C.line}`, flexDirection: 'row', justifyContent: 'flex-end' },
    totalsInner: { width: 180 },
    tRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
    tLabel: { color: C.muted, fontSize: 8 },
    tVal: { color: C.dark, fontFamily: 'Helvetica', fontSize: 8, textAlign: 'right' },
    grandRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.line}` },
    grandVal: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.black },

    notesSection: { padding: '10 35 6 35' },
    notesTitle: { fontFamily: 'Helvetica-Bold', fontSize: 8, color: C.black, marginBottom: 3 },
    notesText: { fontSize: 8, color: C.muted },

    disclaimer: { padding: '8 35 4 35', fontSize: 7, color: C.label },
    footerText: { padding: '8 35 10 35', fontSize: 7, color: C.label, textAlign: 'center' },
    thankYou: { padding: '10 35', textAlign: 'center', fontSize: 8, color: C.muted, fontFamily: 'Helvetica' },
});

const fmt = (v: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(v) || 0);
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

export const QuotePDF = ({ quote, logoBase64 }: { quote: any; logoBase64?: string }) => {
    const b = quote.branding_snapshot || {};
    const accent = b.invoice_accent_color || DEFAULT_DOCUMENT_ACCENT;
    const isMinimal = b.invoice_theme === 'minimal';
    const name = b.business_name || 'Your Workshop';
    const hasLogo = !!(logoBase64 || b.logo_url);

    const issued = quote.issued_at || quote.created_at || new Date().toISOString();
    // Quotes are valid for a fixed window from issue.
    const validDays = b.quote_validity_days || 30;
    let validUntil = quote.valid_until;
    if (!validUntil) {
        const d = new Date(issued);
        d.setDate(d.getDate() + validDays);
        validUntil = d.toISOString();
    }

    const lineItems = quote.line_items || [];
    const veh = quote.vehicle || {};
    const vehMileage = mileageLine(veh.mileage, veh.mileageSource, veh.mileageMotDate);
    const cust = quote.customer || {};

    return (
        <Document>
            <Page size="A4" style={s.page}>
                <PdfPageFooter reference={quote.quote_number} branded={true} />

                <View style={s.continuationHeader} fixed render={({ pageNumber }) => pageNumber > 1 ? (
                    <>
                        <Text style={s.continuationText}>{name} — {quote.quote_number} (continued)</Text>
                        <Text style={s.continuationText}>Total: {fmt(quote.total_gross)}</Text>
                    </>
                ) : null} />

                {/* HEADER */}
                <View style={[s.header, { borderTopWidth: isMinimal ? 3 : 6, borderTopColor: accent }]}>
                    {hasLogo && (
                        <View style={s.logoRow}>
                            <View style={s.logoBox}>
                                <Image src={logoBase64 || b.logo_url} style={s.logo} />
                            </View>
                        </View>
                    )}

                    <View style={s.row}>
                        <View style={s.col1Left}>
                            <Text style={s.label}>Quote From:</Text>
                            <Text style={s.h1}>{name}</Text>
                            {b.address && <Text style={s.sm}>{String(b.address).replace(/\\n/g, '\n')}</Text>}
                            {b.phone && <Text style={s.sm}>{b.phone}</Text>}
                            {b.email && <Text style={s.sm}>{b.email}</Text>}
                            {b.vat_number && <Text style={s.vat}>VAT: {b.vat_number}</Text>}
                        </View>

                        <View style={s.col2Mid}>
                            <Text style={s.label}>Quote For:</Text>
                            <Text style={s.h2}>{cust.full_name || 'Customer'}</Text>
                            {cust.email && <Text style={s.sm}>{cust.email}</Text>}
                            {cust.phone && <Text style={s.sm}>{cust.phone}</Text>}
                            {(veh.vrm || veh.make) && (
                                <Text style={s.vehicleLine}>
                                    Vehicle: <Text style={s.vrm}>{veh.vrm}</Text>
                                    {(veh.make || veh.model) ? ` (${[veh.make, veh.model].filter(Boolean).join(' ')}${veh.engine ? ` - ${veh.engine}` : ''}${veh.fuel_type ? ` (${veh.fuel_type})` : ''})` : ''}
                                    {vehMileage ? `  ·  Mileage: ${vehMileage}` : ''}
                                </Text>
                            )}
                        </View>

                        <View style={s.colLast}>
                            <Text style={s.docTitle}>QUOTE</Text>
                            <Text style={s.docNumber}>{quote.quote_number}</Text>
                            <View style={s.metaBox}>
                                <View style={s.metaRow}>
                                    <Text style={s.metaLabel}>Date:</Text>
                                    <Text style={s.metaVal}>{fmtDate(issued)}</Text>
                                </View>
                                <View style={s.metaRow}>
                                    <Text style={s.metaLabel}>Valid Until:</Text>
                                    <Text style={s.metaVal}>{fmtDate(validUntil)}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>

                {/* TABLE */}
                <View style={s.table}>
                    <View style={s.thead}>
                        <Text style={[s.th, s.cDesc]}>Description</Text>
                        <Text style={[s.th, s.cQty]}>Qty</Text>
                        <Text style={[s.th, s.cPrice]}>Unit Price</Text>
                        <Text style={[s.th, s.cTotal]}>Total</Text>
                    </View>

                    {lineItems.map((item: any, i: number) => (
                        <View key={i} style={s.tr} wrap={false}>
                            <View style={s.cDesc}>
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <Text style={s.itemName}>{item.description}</Text>
                                    {item.item_type && (
                                        <View style={[s.badge, { backgroundColor: `${accent}18` }]}>
                                            <Text style={[s.badgeText, { color: accent }]}>{item.item_type}</Text>
                                        </View>
                                    )}
                                </View>
                            </View>
                            <Text style={[s.cQty, { fontFamily: 'Helvetica', color: C.body }]}>{Number(item.quantity).toFixed(2)}</Text>
                            <Text style={[s.cPrice, { fontFamily: 'Helvetica', color: C.body }]}>{fmt(item.unit_price)}</Text>
                            <Text style={[s.cTotal, { fontFamily: 'Helvetica-Bold', color: C.black }]}>{fmt(item.line_total)}</Text>
                        </View>
                    ))}
                </View>

                {/* TOTALS */}
                <View style={s.totalsBox} wrap={false}>
                    <View style={s.totalsInner}>
                        <View style={s.tRow}>
                            <Text style={s.tLabel}>Subtotal</Text>
                            <Text style={s.tVal}>{fmt(quote.subtotal)}</Text>
                        </View>
                        {quote.vat_amount > 0 && (
                            <View style={s.tRow}>
                                <Text style={s.tLabel}>VAT</Text>
                                <Text style={s.tVal}>{fmt(quote.vat_amount)}</Text>
                            </View>
                        )}
                        <View style={s.grandRow}>
                            <Text style={s.grandVal}>Total</Text>
                            <Text style={s.grandVal}>{fmt(quote.total_gross)}</Text>
                        </View>
                    </View>
                </View>

                {/* NOTES */}
                {quote.notes && (
                    <View style={s.notesSection} wrap={false}>
                        <Text style={s.notesTitle}>Notes:</Text>
                        <Text style={s.notesText}>{quote.notes}</Text>
                    </View>
                )}

                <View style={s.disclaimer}>
                    <Text>This is a quotation, not an invoice. Prices are valid until {fmtDate(validUntil)} and are subject to inspection.</Text>
                </View>

                {b.invoice_footer_text && (
                    <View style={s.footerText}>
                        <Text style={{ textAlign: 'center' }}>{b.invoice_footer_text}</Text>
                    </View>
                )}

                <View style={s.thankYou}>
                    <Text>Thank you — we look forward to working with you.</Text>
                </View>
            </Page>
        </Document>
    );
};
